/** Recruitment Network · pan/zoom canvas.
 *
 *  A deliberately small viewport rather than a graph dependency: the network is a few dozen
 *  absolutely-positioned nodes plus one SVG edge layer, and all it needs is pan, wheel/button
 *  zoom, fit-to-bounds and node dragging. The viewport transform lives HERE, so panning and
 *  zooming re-render only this component — `children` (the nodes) are the same elements between
 *  renders and React skips them. */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import type { ReactNode } from "react";
import { boundsOf, clamp } from "./networkModel.js";
import type { LayoutNode } from "./networkModel.js";

export interface GraphHandle {
  zoomBy: (factor: number) => void;
  fit: () => void;
}

interface View {
  x: number;
  y: number;
  k: number;
}

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 2;
const DRAG_THRESHOLD = 4;

interface Props {
  nodes: LayoutNode[];
  /** Changing this re-fits the viewport (requisition/mode/stage/page/reset changed). */
  fitKey: string;
  /** Lowest zoom an automatic fit may choose — below it the graph is aligned top-left and the
   *  recruiter pans, rather than shrinking cards past legibility. */
  minFitZoom?: number;
  /** Screen pixels at the top kept clear by an automatic fit (the floating toolbar sits there). */
  insetTop?: number;
  onSize: (w: number, h: number) => void;
  onNodeMove?: (id: string, x: number, y: number) => void;
  onBackgroundClick?: () => void;
  /** Fired when the viewport starts moving — screen-anchored overlays (the hover card) close. */
  onViewportInteract?: () => void;
  /** Floating UI drawn above the world, in screen space. */
  overlay?: ReactNode;
  children: ReactNode;
}

export const GraphCanvas = forwardRef<GraphHandle, Props>(function GraphCanvas(
  { nodes, fitKey, minFitZoom = 0.6, insetTop = 0, onSize, onNodeMove, onBackgroundClick, onViewportInteract, overlay, children },
  ref,
) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<View>({ x: 0, y: 0, k: 1 });
  const [animate, setAnimate] = useState(false);
  const [size, setSize] = useState({ w: 0, h: 0 });

  // refs so window-level pointer handlers and the imperative API always see current values
  const viewRef = useRef(view);
  viewRef.current = view;
  const sizeRef = useRef(size);
  sizeRef.current = size;
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const cb = useRef({ onNodeMove, onBackgroundClick, onViewportInteract });
  cb.current = { onNodeMove, onBackgroundClick, onViewportInteract };
  const suppressClick = useRef(false);

  const animateTo = useCallback((next: View) => {
    setAnimate(true);
    setView(next);
  }, []);

  const fit = useCallback(
    (anim: boolean) => {
      const { w, h } = sizeRef.current;
      if (!w || !h) return;
      const b = boundsOf(nodesRef.current);
      const pad = 40;
      const top = pad + insetTop; // fit into the band below the toolbar
      const availH = h - top - pad;
      const k = clamp(Math.min((w - pad * 2) / b.w, availH / b.h, 1), minFitZoom, MAX_ZOOM);
      const x = b.w * k <= w - pad * 2 ? (w - b.w * k) / 2 - b.x * k : pad - b.x * k;
      const y = b.h * k <= availH ? top + (availH - b.h * k) / 2 - b.y * k : top - b.y * k;
      if (anim) animateTo({ x, y, k });
      else setView({ x, y, k });
    },
    [animateTo, minFitZoom, insetTop],
  );

  const zoomAt = useCallback((factor: number, cx: number, cy: number, anim: boolean) => {
    const v = viewRef.current;
    const k = clamp(v.k * factor, MIN_ZOOM, MAX_ZOOM);
    const next = { k, x: cx - (cx - v.x) * (k / v.k), y: cy - (cy - v.y) * (k / v.k) };
    if (anim) animateTo(next);
    else setView(next);
  }, [animateTo]);

  useImperativeHandle(
    ref,
    () => ({
      zoomBy: (f) => {
        cb.current.onViewportInteract?.();
        zoomAt(f, sizeRef.current.w / 2, sizeRef.current.h / 2, true);
      },
      fit: () => fit(true),
    }),
    [fit, zoomAt],
  );

  // measure — the layout is computed from the real canvas size, never an assumed screen
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const w = Math.round(entry.contentRect.width);
      const h = Math.round(entry.contentRect.height);
      setSize((s) => (s.w === w && s.h === h ? s : { w, h }));
      onSize(w, h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [onSize]);

  // auto-fit on first measure, on data/mode changes and on resize. First fit is instant.
  const fitted = useRef(false);
  useEffect(() => {
    if (!size.w || !size.h) return;
    fit(fitted.current);
    fitted.current = true;
  }, [fitKey, size.w, size.h, fit]);

  // wheel zoom around the cursor (non-passive so the page itself doesn't scroll)
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      cb.current.onViewportInteract?.();
      setAnimate(false);
      const r = el.getBoundingClientRect();
      zoomAt(Math.exp(-e.deltaY * 0.0015), e.clientX - r.left, e.clientY - r.top, false);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomAt]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest("[data-no-pan]")) return;
    const nodeEl = target.closest<HTMLElement>("[data-node-id]");
    const nodeId = nodeEl?.dataset.draggable === "1" ? nodeEl.dataset.nodeId : undefined;
    const base = nodeId ? nodesRef.current.find((n) => n.id === nodeId) : undefined;
    const startX = e.clientX;
    const startY = e.clientY;
    const v0 = viewRef.current;
    let moved = false;
    setAnimate(false);

    const move = (ev: PointerEvent): void => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (!moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      if (!moved) {
        moved = true;
        cb.current.onViewportInteract?.();
        boxRef.current?.classList.add(base ? "is-dragging-node" : "is-panning");
      }
      if (base) cb.current.onNodeMove?.(base.id, base.x + dx / v0.k, base.y + dy / v0.k);
      else if (!nodeEl) setView({ ...v0, x: v0.x + dx, y: v0.y + dy });
    };
    const up = (): void => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      boxRef.current?.classList.remove("is-dragging-node", "is-panning");
      if (moved) {
        // the click that follows a drag must not select the node / clear the selection
        suppressClick.current = true;
        setTimeout(() => (suppressClick.current = false), 0);
      } else if (!nodeEl) {
        cb.current.onBackgroundClick?.();
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const gridSize = 26 * view.k;
  return (
    <div
      ref={boxRef}
      className="rn-canvas"
      onPointerDown={onPointerDown}
      onClickCapture={(e) => {
        if (suppressClick.current) {
          e.stopPropagation();
          e.preventDefault();
        }
      }}
    >
      <div
        className="rn-grid"
        aria-hidden="true"
        style={{
          backgroundSize: `${gridSize}px ${gridSize}px`,
          backgroundPosition: `${view.x}px ${view.y}px`,
        }}
      />
      <div
        className={`rn-world${animate ? " is-animating" : ""}`}
        style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})` }}
        onTransitionEnd={(e) => {
          if (e.target === e.currentTarget) setAnimate(false);
        }}
      >
        {children}
      </div>
      {overlay}
    </div>
  );
});
